/**
 * NotificationPanelMobile - Mobile notification bottom sheet
 * 
 * Features:
 * - Bottom sheet pattern (common on mobile)
 * - Touch-friendly interactions
 * - Pull to refresh support
 * - Infinite scroll
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import NotificationItem from '../NotificationItem';
import '../../styles/Notifications.css';

const NotificationPanelMobile = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const listRef = useRef(null);
  const contentRef = useRef(null);
  
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    markAsRead,
    markAllAsRead,
    loadMore,
  } = useNotifications();

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !isLoading) {
      loadMore();
    }
  }, [hasMore, isLoading, loadMore]);

  // Navigate to notification target
  const handleNotificationClick = useCallback((notification) => {
    // Mark as read immediately on click
    if (!notification.is_read) {
      markAsRead([notification.id]);
    }

    // Check if this is a chat message notification
    if (notification.extra_data?.type === 'chat_message' && notification.extra_data?.chat_id) {
      navigate('/chat', { state: { chatId: notification.extra_data.chat_id } });
    } else {
      // Navigate based on notification type
      const targetId = notification.target_id || notification.extra_data?.trip_id;
      const targetType = notification.target_type;
      
      if (targetType === 'post' && targetId) {
        navigate(`/posts/${targetId}`);
      } else if (targetType === 'trip' && targetId) {
        if (notification.verb === 'trip_request') {
          navigate(`/companions/${targetId}/candidates`);
        } else {
          navigate(`/companions/${targetId}`);
        }
      } else if (notification.extra_data?.post_id) {
        navigate(`/posts/${notification.extra_data.post_id}`);
      }
    }
    
    onClose();
  }, [navigate, markAsRead, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="notification-panel-mobile"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Уведомления"
    >
      <div className="notification-panel-mobile__backdrop" onClick={onClose} />
      
      <div className="notification-panel-mobile__content" ref={contentRef}>
        {/* Pull handle */}
        <div className="notification-panel-mobile__handle" />
        
        {/* Header */}
        <div className="notification-panel-mobile__header">
          <h2 className="notification-panel-mobile__title">
            Уведомления
            {unreadCount > 0 && (
              <span className="notification-dropdown__count">{unreadCount}</span>
            )}
          </h2>
          <button 
            className="notification-panel-mobile__close"
            onClick={onClose}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        
        {/* Mark all read button */}
        {unreadCount > 0 && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <button 
              className="notification-dropdown__mark-all"
              onClick={markAllAsRead}
              style={{ width: '100%', textAlign: 'center', padding: '10px' }}
            >
              Отметить все как прочитанные
            </button>
          </div>
        )}
        
        {/* Notification list */}
        <div 
          className="notification-panel-mobile__list"
          ref={listRef}
          onScroll={handleScroll}
        >
          {notifications.length === 0 && !isLoading ? (
            <div className="notification-dropdown__empty">
              <div className="notification-dropdown__empty-icon">🔔</div>
              <p>Нет уведомлений</p>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))}
              
              {isLoading && (
                <div className="notification-dropdown__loading">
                  <div className="notification-spinner"></div>
                </div>
              )}
              
              {!hasMore && notifications.length > 0 && (
                <div className="notification-dropdown__end">
                  Это все уведомления
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanelMobile;

